import { Box, Button, Typography, type SxProps, type Theme } from "@mui/material";
import type { LucideIcon } from "lucide-react";
import React from "react";

export const experienceFadeInSx = {
  animation: "experienceFadeIn 0.38s cubic-bezier(0.22, 1, 0.36, 1)",
  "@keyframes experienceFadeIn": {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

export type ExperienceMomentVariant = "standard" | "season";

export function isSeasonMomentVariant(
  variant?: ExperienceMomentVariant,
): variant is "season" {
  return variant === "season";
}

export function ExperienceOptionalLabel({
  children = "Optional",
  darkMode,
}: {
  children?: string;
  darkMode: boolean;
}) {
  const theme = getExperienceTheme(darkMode);
  return (
    <Typography
      component="span"
      sx={{
        ml: 0.75,
        fontSize: "0.68rem",
        fontWeight: 600,
        letterSpacing: "0.04em",
        color: theme.textMuted,
        textTransform: "uppercase",
      }}
    >
      {children}
    </Typography>
  );
}

export type ExperienceTheme = {
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  border: string;
  borderHover: string;
  surface: string;
  surfaceMuted: string;
  surfaceElevated: string;
  accent: string;
  accentHover: string;
  destructive: string;
  uploadBorder: string;
  uploadBg: string;
  uploadBgHover: string;
};

export function getExperienceTheme(darkMode: boolean): ExperienceTheme {
  return {
    textPrimary: darkMode ? "var(--neutral-100)" : "var(--neutral-900)",
    textSecondary: darkMode ? "var(--neutral-300)" : "var(--neutral-600)",
    textMuted: darkMode ? "var(--neutral-400)" : "var(--neutral-500)",
    border: darkMode ? "var(--neutral-700)" : "var(--neutral-200)",
    borderHover: darkMode ? "var(--primary-500)" : "var(--primary-400)",
    surface: darkMode ? "var(--neutral-800)" : "var(--neutralWhite)",
    surfaceMuted: darkMode ? "var(--neutral-900)" : "var(--neutral-50)",
    surfaceElevated: darkMode ? "var(--neutral-800)" : "var(--neutral-100)",
    accent: darkMode ? "var(--primary-600)" : "var(--primary-500)",
    accentHover: darkMode ? "var(--primary-700)" : "var(--primary-600)",
    destructive: darkMode
      ? "var(--destructive-400)"
      : "var(--destructive-700)",
    uploadBorder: darkMode ? "var(--neutral-700)" : "var(--primary-300)",
    uploadBg: darkMode ? "var(--neutral-800)" : "var(--neutral-100)",
    uploadBgHover: darkMode
      ? "rgba(99, 102, 241, 0.08)"
      : "rgba(99, 102, 241, 0.06)",
  };
}

export function fieldHelperText(
  error?: string,
  hint?: string
): string {
  return error || hint || " ";
}

export function ExperienceFieldLabel({
  children,
  darkMode,
}: {
  children: React.ReactNode;
  darkMode: boolean;
}) {
  const theme = getExperienceTheme(darkMode);
  return (
    <Typography
      sx={{
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: theme.textMuted,
        mt: 0.5,
        mb: 0.25,
      }}
    >
      {children}
    </Typography>
  );
}

export function ExperienceFieldGuide({
  children,
  darkMode,
}: {
  children: React.ReactNode;
  darkMode: boolean;
}) {
  const theme = getExperienceTheme(darkMode);
  return (
    <Typography
      variant="body2"
      sx={{
        color: theme.textMuted,
        lineHeight: 1.55,
        fontSize: "0.875rem",
      }}
    >
      {children}
    </Typography>
  );
}

export function getExperienceFieldSx(darkMode: boolean): SxProps<Theme> {
  const theme = getExperienceTheme(darkMode);
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      bgcolor: theme.surface,
      color: theme.textPrimary,
      transition: "box-shadow 0.2s ease, border-color 0.2s ease",
      "& fieldset": { borderColor: theme.border },
      "&:hover fieldset": { borderColor: theme.borderHover },
      "&.Mui-focused fieldset": {
        borderColor: theme.accent,
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.textSecondary,
      "&.Mui-focused": { color: theme.accent },
    },
    "& input::placeholder, & textarea::placeholder": {
      color: theme.textMuted,
      opacity: 1,
    },
    "& .MuiFormHelperText-root": { color: theme.textMuted },
    "& .MuiFormHelperText-root.Mui-error": { color: theme.destructive },
  };
}

export function getPrimaryButtonSx(darkMode: boolean): SxProps<Theme> {
  const theme = getExperienceTheme(darkMode);
  return {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "999px",
    px: 2.5,
    bgcolor: theme.accent,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    transition: "all 0.2s ease",
    "&:hover": {
      bgcolor: theme.accentHover,
      boxShadow: "0 4px 14px rgba(0,0,0,0.14)",
      transform: "translateY(-1px)",
    },
  };
}

export function getFloatingActionSx(darkMode: boolean): SxProps<Theme> {
  return {
    borderRadius: "999px",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.8rem",
    px: 1.75,
    py: 0.65,
    backdropFilter: "blur(12px)",
    bgcolor: darkMode ? "rgba(0,0,0,0.72)" : "rgba(255,255,255,0.92)",
    color: darkMode ? "var(--neutral-100)" : "var(--neutral-900)",
    border: `1px solid ${
      darkMode ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.08)"
    }`,
    boxShadow: darkMode
      ? "0 6px 18px rgba(0,0,0,0.35)"
      : "0 4px 14px rgba(0,0,0,0.1)",
    transition: "all 0.2s ease",
    "&:hover": {
      bgcolor: darkMode ? "rgba(0,0,0,0.88)" : "rgba(255,255,255,1)",
      transform: "scale(1.03)",
    },
  };
}

type ExperienceShellProps = {
  children: React.ReactNode;
  gap?: number;
};

export function ExperienceShell({ children, gap = 2.5 }: ExperienceShellProps) {
  return (
    <Box
      sx={{
        width: "100%",
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        p: { xs: 2, sm: 3 },
        gap,
        overflow: "visible",
        ...experienceFadeInSx,
      }}
    >
      {children}
    </Box>
  );
}

type ExperienceHeaderProps = {
  title: string;
  subtitle?: string;
  darkMode: boolean;
  icon?: LucideIcon;
};

export function ExperienceHeader({
  title,
  subtitle,
  darkMode,
  icon: Icon,
}: ExperienceHeaderProps) {
  const theme = getExperienceTheme(darkMode);
  return (
    <Box sx={{ mb: 0.5, flexShrink: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: subtitle ? 0.5 : 0 }}>
        {Icon && (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: darkMode
                ? "rgba(99, 102, 241, 0.15)"
                : "rgba(99, 102, 241, 0.1)",
              color: theme.accent,
              flexShrink: 0,
            }}
          >
            <Icon size={18} strokeWidth={2} />
          </Box>
        )}
        <Typography
          variant="h6"
          sx={{
            color: theme.textPrimary,
            fontWeight: 700,
            fontSize: { xs: "1rem", sm: "1.12rem" },
            lineHeight: 1.25,
          }}
        >
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Typography
          variant="body2"
          sx={{
            color: theme.textMuted,
            lineHeight: 1.5,
            pl: Icon ? 5.5 : 0,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

type ExperienceSectionProps = {
  label: string;
  children: React.ReactNode;
  darkMode: boolean;
};

export function ExperienceSection({
  label,
  children,
  darkMode,
}: ExperienceSectionProps) {
  const theme = getExperienceTheme(darkMode);
  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: theme.textMuted,
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          p: 2,
          borderRadius: "14px",
          border: `1px solid ${theme.border}`,
          bgcolor: theme.surface,
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

type ExperienceUploadZoneProps = {
  darkMode: boolean;
  hasError?: boolean;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  onFileSelect: (files: FileList | null) => void;
  accept?: string;
  inputId: string;
  buttonLabel: string;
  minHeight?: number;
};

export function ExperienceUploadZone({
  darkMode,
  hasError,
  icon: Icon,
  title,
  subtitle,
  onFileSelect,
  accept,
  inputId,
  buttonLabel,
  minHeight = 180,
}: ExperienceUploadZoneProps) {
  const theme = getExperienceTheme(darkMode);
  const [isDragging, setIsDragging] = React.useState(false);

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        onFileSelect(e.dataTransfer.files);
      }}
      sx={{
        width: "100%",
        minHeight,
        p: 3,
        borderRadius: "16px",
        border: `2px dashed ${
          hasError
            ? darkMode
              ? "var(--destructive-500)"
              : "var(--destructive-600)"
            : isDragging
              ? theme.accent
              : theme.uploadBorder
        }`,
        bgcolor: isDragging ? theme.uploadBgHover : theme.uploadBg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.25,
        cursor: "pointer",
        transition: "all 0.22s ease",
        transform: isDragging ? "scale(1.01)" : "none",
        "&:hover": {
          borderColor: theme.borderHover,
          bgcolor: theme.uploadBgHover,
        },
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: theme.surface,
          border: `1px solid ${theme.border}`,
          color: theme.accent,
          transition: "transform 0.2s ease",
          ...(isDragging && { transform: "scale(1.08)" }),
        }}
      >
        <Icon size={26} strokeWidth={1.75} />
      </Box>
      <Typography
        variant="subtitle2"
        sx={{
          color: theme.textPrimary,
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: theme.textMuted,
          textAlign: "center",
          maxWidth: 360,
          lineHeight: 1.45,
        }}
      >
        {subtitle}
      </Typography>
      <input
        type="file"
        accept={accept}
        id={inputId}
        hidden
        onChange={(e) => onFileSelect(e.target.files)}
      />
      <label htmlFor={inputId}>
        <Button
          variant="contained"
          component="span"
          sx={{ ...getPrimaryButtonSx(darkMode), mt: 0.5 }}
        >
          {buttonLabel}
        </Button>
      </label>
    </Box>
  );
}

type ExperienceMediaCardProps = {
  darkMode: boolean;
  children: React.ReactNode;
  actions?: React.ReactNode;
};

export function ExperienceMediaCard({
  darkMode,
  children,
  actions,
}: ExperienceMediaCardProps) {
  const theme = getExperienceTheme(darkMode);
  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden",
        bgcolor: theme.surface,
        border: `1px solid ${theme.border}`,
        boxShadow: darkMode
          ? "0 4px 20px rgba(0,0,0,0.25)"
          : "0 4px 16px rgba(0,0,0,0.06)",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: darkMode
            ? "0 8px 28px rgba(0,0,0,0.35)"
            : "0 8px 24px rgba(0,0,0,0.1)",
        },
      }}
    >
      {actions && (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 2,
            display: "flex",
            gap: 1,
          }}
        >
          {actions}
        </Box>
      )}
      {children}
    </Box>
  );
}

type ExperienceErrorTextProps = {
  darkMode: boolean;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
};

export function ExperienceErrorText({
  darkMode,
  children,
  sx,
}: ExperienceErrorTextProps) {
  const theme = getExperienceTheme(darkMode);
  return (
    <Typography
      variant="caption"
      sx={{ color: theme.destructive, display: "block", ...sx }}
    >
      {children}
    </Typography>
  );
}

type ExperienceClickCardProps = {
  darkMode: boolean;
  label: string;
  value?: string;
  placeholder: string;
  hasError?: boolean;
  onClick: () => void;
  multiline?: boolean;
};

export function ExperienceClickCard({
  darkMode,
  label,
  value,
  placeholder,
  hasError,
  onClick,
  multiline,
}: ExperienceClickCardProps) {
  const theme = getExperienceTheme(darkMode);
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        p: 2,
        borderRadius: "14px",
        border: `1px solid ${
          hasError
            ? darkMode
              ? "var(--destructive-500)"
              : "var(--destructive-600)"
            : theme.border
        }`,
        bgcolor: theme.surface,
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: theme.borderHover,
          transform: "translateY(-2px)",
          boxShadow: darkMode
            ? "0 6px 20px rgba(0,0,0,0.2)"
            : "0 4px 16px rgba(0,0,0,0.06)",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.textMuted,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          display: "block",
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant={multiline ? "body2" : "body1"}
        sx={{
          color: value ? theme.textPrimary : theme.textMuted,
          fontStyle: value ? "normal" : "italic",
          lineHeight: 1.5,
        }}
      >
        {value || placeholder}
      </Typography>
    </Box>
  );
}

export function getExperienceModalSx(darkMode: boolean): SxProps<Theme> {
  return {
    width: "100%",
    maxWidth: 600,
    bgcolor: darkMode ? "var(--neutral-900)" : "var(--neutralWhite)",
    borderRadius: "20px",
    p: 3,
    position: "relative",
    maxHeight: "90vh",
    overflow: "auto",
    border: `1px solid ${
      darkMode ? "var(--neutral-700)" : "var(--neutral-200)"
    }`,
    boxShadow: darkMode
      ? "0 24px 64px rgba(0,0,0,0.5)"
      : "0 24px 64px rgba(0,0,0,0.12)",
    ...experienceFadeInSx,
  };
}
